import { useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { formatoLabel } from "@/lib/ped";
import { Panel } from "@/components/core/AppLayout";
import { ChannelIcons } from "@/components/ped/PedBits";

const H = ({ children }) => <h4 className="text-xs font-bold uppercase tracking-wider text-[#E8173A] mt-6 mb-2">{children}</h4>;
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const Ideas = ({ campaign: c, onChange }) => {
  const done = new Set((c.post_creati || []).map((p) => p.indice));
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (i) => setSel((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]));
  const create = async () => {
    setBusy(true);
    try { const r = await api.post(`/campagne/${c.id}/crea-post`, { indici: sel }); toast.success(`${r.data.created} post creati come bozze nel PED`); setSel([]); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="campaign-ideas">
      {c.idee_contenuti.map((i, idx) => (
        <label key={idx} className={`flex gap-3 rounded-xl p-3 mb-2 ${done.has(idx) ? "bg-emerald-50/60" : "bg-[#A6DAFC] cursor-pointer"}`} data-testid={`campaign-idea-${idx}`}>
          {done.has(idx) ? <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5" /> : <Checkbox checked={sel.includes(idx)} onCheckedChange={() => toggle(idx)} data-testid={`campaign-idea-check-${idx}`} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-bold text-[#0A1128]">{i.data} {i.ora} <ChannelIcons canali={[i.canale]} size={13} /> {formatoLabel(i.formato)}{done.has(idx) && <span className="text-emerald-700">· già nel PED</span>}</div>
            <div className="font-bold text-[#0A1128]">{i.tema}</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-3">{i.caption}</div>
          </div>
        </label>
      ))}
      <Button onClick={create} disabled={!sel.length || busy} className="rounded-full bg-[#E8173A] hover:bg-[#C8102E] font-bold mt-2" data-testid="campaign-create-posts">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarPlus className="h-4 w-4 mr-1.5" />}Crea {sel.length || ""} post in PED come bozze
      </Button>
    </div>
  );
};

export const CampaignView = ({ campaign: c, onChange }) => (
  <Panel className="p-8" data-testid="campaign-view">
    <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8173A]">Campagna · {eur(c.input?.budget)} · {c.input?.durata} dal {c.input?.data_inizio}</div>
    <h3 className="font-display text-2xl font-black text-[#0A1128] mt-1" data-testid="campaign-title">{c.titolo}</h3>
    <H>Concept</H><p className="text-[#0A1128]" data-testid="campaign-concept">{c.concept}</p>
    {c.messaggio_chiave && <><H>Messaggio chiave</H><p className="font-display text-lg font-extrabold text-[#0A1128]">«{c.messaggio_chiave}»</p></>}
    <H>Canali e ripartizione budget</H>
    <div className="space-y-2" data-testid="campaign-budget-split">
      {c.canali.map((ch, i) => (
        <div key={i} className="flex items-start gap-3 text-sm"><ChannelIcons canali={[ch.canale]} size={16} /><span className="font-bold w-20 text-[#0A1128]">{eur(ch.budget)}</span><span className="text-slate-700 flex-1">{ch.motivazione}</span></div>
      ))}
    </div>
    <H>Calendario fasi</H>
    <ol className="space-y-2">{c.fasi.map((f, i) => <li key={i} className="text-sm text-[#0A1128]"><span className="font-bold">{f.nome}</span> <span className="text-slate-600">({f.periodo})</span> – {f.attivita}</li>)}</ol>
    <H>KPI</H>
    <div className="grid grid-cols-2 gap-2">{c.kpi.map((k, i) => <div key={i} className="rounded-xl bg-[#A6DAFC] px-3 py-2 text-sm"><div className="font-bold text-[#0A1128]">{k.nome}</div><div className="text-slate-700">{k.obiettivo}</div></div>)}</div>
    <H>Idee contenuti</H>
    <Ideas key={c.id} campaign={c} onChange={onChange} />
  </Panel>
);
